import React, { useState, useEffect, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";

import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Link from "@material-ui/core/Link";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import pageTitle from "../utils/pageTitle";
import { showErrorAlert } from "../store/actions/alerts";
import ComponentLoader from "../components/loader/ComponentLoader";

import makeApiRequest from "../utils/axios/new-axios";

import {
  COLOR_RED,
  COLOR_GREEN,
  COLOR_BROWN,
  COLOR_BLUE,
  COLOR_GRAY,
  DASHBOARD_STATS_URL,
} from "../config";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    marginBottom: "20px",
  },
  paper: {
    height: 90,
    width: 90,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    margin: theme.spacing(1),
    textAlign: "center",
    color: "theme.palette.text.secondary",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-evenly",
    flexWrap: "wrap"
  },
  box: {
    //backgroundColor: "#212121",
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  group: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-evenly"
  }
}));

export default function Dashboard() {
  pageTitle("Dashboard");
  const dispatch = useDispatch();
  const classes = useStyles();
  const [isLoading, setIsloading] = useState(true);
  const [values, setValues] = useState(null);

  const searchUrl = `/location/search`;
  const travalourUrl = `/travalour/search`;
  const travelCardUrl = `/travel-card/search`;
  const tripUrl = `/trip/search`;
  const spamUrl = `/spam`;
  const accountDelReqUrl = "/travalour/account-del-req";
  const feedbackUrl = "/feedback"

  useEffect(() => {
    makeApiRequest({
      url: DASHBOARD_STATS_URL.url,
      method: DASHBOARD_STATS_URL.method,
    })
      .then((response) => {
        setIsloading(false);
        setValues(response.data);
      })
      .catch((error) => {
        setIsloading(false);
        dispatch(
          showErrorAlert(
            "Location Fetch Error : " +
            (!!error.response
              ? `${error.response.status}! - ${error.response.data.errorMessage}`
              : error.message)
          )
        );
      });
    // eslint-disable-next-line
  }, []); // [] is required to make sure that it runs only once


  const Tile = ({ title, value, url, bgColor }) => {
    return (
      <Paper
        className={classes.paper}
        style={{ backgroundColor: bgColor }}
      >
        <Typography variant="caption" display="block" gutterBottom>
          {title}
        </Typography>
        {!!url ? (<Link
          color="inherit"
          component={RouterLink}
          to={url}
          variant="h6"
          underline="none"
        >
          {value}
        </Link>) : (<Typography variant="h6" gutterBottom>
          {value}
        </Typography>)}

      </Paper>
    )
  }

  return (
    <Fragment>
      <ComponentLoader isLoading={isLoading} />
      {!isLoading && !!values && (
        <Container style={{ padding: "10px" }}>
          {/* Spam Reports */}
          <Paper className={classes.root}>
            <Box className={classes.row}>
              <Box className={classes.box}>
                <Typography variant="subtitle1" align="center">
                  User Spam Reports (Pending)
                </Typography>
                <Box className={classes.group}>
                  <Tile
                    title="Total"
                    value={!!values.spamPending && values.spamPending.user
                      ? values.spamPending.user.total
                      : 0}
                    bgColor={COLOR_RED}
                    url={`${spamUrl}?status=0&entity=52`}
                  />
                  <Tile
                    title="Today"
                    value={!!values.spamPending && values.spamPending.user
                      ? values.spamPending.user.day
                      : 0}
                    bgColor={COLOR_RED}
                    url={`${spamUrl}?status=0&entity=52`}
                  />
                </Box>
              </Box>
              <Box className={classes.box}>
                <Typography variant="subtitle1" align="center">
                  Travel Card Spam Reports (Pending)
                </Typography>
                <Box className={classes.group}>
                  <Tile
                    title="Total"
                    value={!!values.spamPending && values.spamPending.travelCard
                      ? values.spamPending.travelCard.total
                      : 0}
                    bgColor={COLOR_RED}
                    url={`${spamUrl}?status=0&entity=48`}
                  />
                  <Tile
                    title="Today"
                    value={!!values.spamPending && values.spamPending.travelCard
                      ? values.spamPending.travelCard.day
                      : 0}
                    bgColor={COLOR_RED}
                    url={`${spamUrl}?status=0&entity=48`}
                  />
                </Box>
              </Box>
              <Box className={classes.box}>
                <Typography variant="subtitle1" align="center">
                  Trip Spam Reports (Pending)
                </Typography>
                <Box className={classes.group}>
                  <Tile
                    title="Total"
                    value={!!values.spamPending && values.spamPending.trip
                      ? values.spamPending.trip.total
                      : 0}
                    bgColor={COLOR_RED}
                    url={`${spamUrl}?status=0&entity=59`}
                  />
                  <Tile
                    title="Today"
                    value={!!values.spamPending && values.spamPending.trip
                      ? values.spamPending.trip.day
                      : 0}
                    bgColor={COLOR_RED}
                    url={`${spamUrl}?status=0&entity=59`}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
          {/* Account Delete & Feedback */}
          <Paper className={classes.root}>
            <Box className={classes.row}>
              <Box className={classes.box}>
                <Typography variant="subtitle1" align="center">
                  Account Delete Request
                </Typography>
                <Box className={classes.group}>
                  <Tile
                    title="New Delete Request"
                    value={!!values.users && values.users.deleteRequest
                      ? values.users.deleteRequest
                      : 0}
                    bgColor={COLOR_RED}
                    url={accountDelReqUrl}
                  />
                </Box>
              </Box>
              <Box className={classes.box}>
                <Typography variant="subtitle1" align="center">
                  User Feedback
                </Typography>
                <Box className={classes.group}>
                  <Tile
                    title="New Feedback"
                    value={values?.userFeedback ?? 0}
                    bgColor={COLOR_BLUE}
                    url={`${feedbackUrl}?status=0&sort=date&order=desc`}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
          {/* Locatons */}
          <Paper className={classes.root}>
            <Box className={classes.row}>
              <Box className={classes.box}>
                <Typography variant="subtitle1" align="center">
                  Locations To Be Verified
                </Typography>
                <Box className={classes.group}>
                  <Tile
                    title="Admin User Created"
                    value={!!values.locations &&
                      values.locations.status &&
                      (values.locations.status.find(
                        (item) => item.status === 2
                      )
                        ? values.locations.status.find(
                          (item) => item.status === 2
                        ).count
                        : 0)}
                    bgColor={COLOR_RED}
                    url={`${searchUrl}?status=2`}
                  />
                  <Tile
                    title="App User Created"
                    value={!!values.locations &&
                      values.locations.status &&
                      (values.locations.status.find(
                        (item) => item.status === 3
                      )
                        ? values.locations.status.find(
                          (item) => item.status === 3
                        ).count
                        : 0)}
                    bgColor={COLOR_RED}
                    url={`${searchUrl}?status=3`}
                  />
                  <Tile
                    title="Without Adm Area"
                    value={(!!values.locations && values.locations.withoutAdm) || 0}
                    bgColor={COLOR_RED}
                    url={`${searchUrl}?admStatus=0`}
                  />
                </Box>
              </Box>
              <Box className={classes.box}>
                <Typography variant="subtitle1" align="center">
                  Location Stats
                </Typography>
                <Box className={classes.group}>
                  <Tile
                    title="Active"
                    value={!!values.locations &&
                      values.locations.status &&
                      (values.locations.status.find(
                        (item) => item.status === 1
                      )
                        ? values.locations.status.find(
                          (item) => item.status === 1
                        ).count
                        : 0)}
                    bgColor={COLOR_GREEN}
                    url={`${searchUrl}?status=1`}
                  />
                  <Tile
                    title="Not to be verified"
                    value={!!values.locations &&
                      values.locations.status &&
                      (values.locations.status.find(
                        (item) => item.status === 4
                      )
                        ? values.locations.status.find(
                          (item) => item.status === 4
                        ).count
                        : 0)}
                    bgColor={COLOR_BROWN}
                    url={`${searchUrl}?status=4`}
                  />
                  <Tile
                    title="In-Active"
                    value={!!values.locations &&
                      values.locations.status &&
                      (values.locations.status.find(
                        (item) => item.status === 0
                      )
                        ? values.locations.status.find(
                          (item) => item.status === 0
                        ).count
                        : 0)}
                    bgColor={COLOR_GRAY}
                    url={`${searchUrl}?status=0`}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
          {/* Users */}
          <Paper className={classes.root}>
            <Box className={classes.row}>
              <Box className={classes.box}>
                <Typography variant="subtitle1" align="center">
                  Users Joined
                </Typography>
                <Box className={classes.group}>
                  <Tile
                    title="Today"
                    value={!!values.users && values.users.joined
                      ? values.users.joined.day
                      : 0}
                    bgColor={COLOR_BLUE}
                    url={`${travalourUrl}?order=desc&sort=date&status=1`}
                  />
                  <Tile
                    title="Last 7 Days"
                    value={!!values.users && values.users.joined
                      ? values.users.joined.week
                      : 0}
                    bgColor={COLOR_BLUE}
                    url={`${travalourUrl}?order=desc&sort=date&status=1`}
                  />
                  <Tile
                    title="Last 30 Days"
                    value={!!values.users && values.users.joined
                      ? values.users.joined.month
                      : 0}
                    bgColor={COLOR_BLUE}
                    url={`${travalourUrl}?order=desc&sort=date&status=1`}
                  />
                </Box>
              </Box>

              <Box className={classes.box}>
                <Typography variant="subtitle1" align="center">
                  User Stats
                </Typography>
                <Box className={classes.group}>
                  <Tile
                    title="Active"
                    value={!!values.users &&
                      values.users.status &&
                      (values.users.status.find((item) => item.status === 1)
                        ? values.users.status.find(
                          (item) => item.status === 1
                        ).count
                        : 0)}
                    bgColor={COLOR_GREEN}
                    url={`${travalourUrl}?status=1`}
                  />
                  <Tile
                    title="Suspended"
                    value={!!values.users &&
                      values.users.status &&
                      (values.users.status.find((item) => item.status === 2)
                        ? values.users.status.find(
                          (item) => item.status === 2
                        ).count
                        : 0)}
                    bgColor={COLOR_BROWN}
                    url={`${travalourUrl}?status=2`}
                  />
                  <Tile
                    title="In-Progress"
                    value={!!values.users &&
                      values.users.status &&
                      (values.users.status.find((item) => item.status === 0)
                        ? values.users.status.find(
                          (item) => item.status === 0
                        ).count
                        : 0)}
                    bgColor={COLOR_GRAY}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
          {/* Travel Cards */}
          <Paper className={classes.root}>
            <Box className={classes.row}>
              <Box className={classes.box}>
                <Typography variant="subtitle1" align="center">
                  Travel Cards (Public) Created
                </Typography>
                <Box className={classes.group}>
                  <Tile
                    title="Today"
                    value={!!values.travelCards && values.travelCards.created
                      ? values.travelCards.created.day
                      : 0}
                    bgColor={COLOR_BLUE}
                    url={`${travelCardUrl}?status=1&sort=date&order=desc`}
                  />
                  <Tile
                    title="Last 7 Days"
                    value={!!values.travelCards && values.travelCards.created
                      ? values.travelCards.created.week
                      : 0}
                    bgColor={COLOR_BLUE}
                    url={`${travelCardUrl}?status=1&sort=date&order=desc`}
                  />
                  <Tile
                    title="Last 30 Days"
                    value={!!values.travelCards && values.travelCards.created
                      ? values.travelCards.created.month
                      : 0}
                    bgColor={COLOR_BLUE}
                    url={`${travelCardUrl}?status=1&sort=date&order=desc`}
                  />
                </Box>
              </Box>
              <Box className={classes.box}>
                <Typography variant="subtitle1" align="center">
                  Travel Cards (Public) Stats
                </Typography>
                <Box className={classes.group}>
                  <Tile
                    title="Active"
                    value={!!values.travelCards &&
                      values.travelCards.status &&
                      (values.travelCards.status.find(
                        (item) => item.status === 1
                      )
                        ? values.travelCards.status.find(
                          (item) => item.status === 1
                        ).count
                        : 0)}
                    bgColor={COLOR_GREEN}
                    url={`${travelCardUrl}?status=1&sort=name&order=asc`}
                  />
                  <Tile
                    title="Suspended"
                    value={!!values.travelCards &&
                      values.travelCards.status &&
                      (values.travelCards.status.find(
                        (item) => item.status === 2
                      )
                        ? values.travelCards.status.find(
                          (item) => item.status === 2
                        ).count
                        : 0)}
                    bgColor={COLOR_BROWN}
                    url={`${travelCardUrl}?status=2&sort=name&order=asc`}
                  />
                  <Tile
                    title="In-Progress"
                    value={!!values.travelCards &&
                      values.travelCards.status &&
                      (values.travelCards.status.find(
                        (item) => item.status === 0
                      )
                        ? values.travelCards.status.find(
                          (item) => item.status === 0
                        ).count
                        : 0)}
                    bgColor={COLOR_GRAY}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
          {/* Trips */}
          <Paper className={classes.root}>
            <Box className={classes.row}>
              <Box className={classes.box}>
                <Typography variant="subtitle1" align="center">
                  Trips (Public) Created
                </Typography>
                <Box className={classes.group}>
                  <Tile
                    title="Today"
                    value={!!values.trips && values.trips.created
                      ? values.trips.created.day
                      : 0}
                    bgColor={COLOR_BLUE}
                    url={`${tripUrl}?status=1&sort=date&order=desc`}
                  />
                  <Tile
                    title="Last 7 Days"
                    value={!!values.trips && values.trips.created
                      ? values.trips.created.week
                      : 0}
                    bgColor={COLOR_BLUE}
                    url={`${tripUrl}?status=1&sort=date&order=desc`}
                  />
                  <Tile
                    title="Last 30 Days"
                    value={!!values.trips && values.trips.created
                      ? values.trips.created.month
                      : 0}
                    bgColor={COLOR_BLUE}
                    url={`${tripUrl}?status=1&sort=date&order=desc`}
                  />
                </Box>
              </Box>
              <Box className={classes.box}>
                <Typography variant="subtitle1" align="center">
                  Trips (Public) Stats
                </Typography>
                <Box className={classes.group}>
                  <Tile
                    title="Active"
                    value={!!values.trips &&
                      values.trips.status &&
                      (values.trips.status.find(
                        (item) => item.status === 1
                      )
                        ? values.trips.status.find(
                          (item) => item.status === 1
                        ).count
                        : 0)}
                    bgColor={COLOR_GREEN}
                    url={`${tripUrl}?status=1&sort=name&order=asc`}
                  />
                  <Tile
                    title="Suspended"
                    value={!!values.trips &&
                      values.trips.status &&
                      (values.trips.status.find(
                        (item) => item.status === 2
                      )
                        ? values.trips.status.find(
                          (item) => item.status === 2
                        ).count
                        : 0)}
                    bgColor={COLOR_BROWN}
                    url={`${tripUrl}?status=2&sort=name&order=asc`}
                  />
                  <Tile
                    title="In-Progress"
                    value={!!values.trips &&
                      values.trips.status &&
                      (values.trips.status.find(
                        (item) => item.status === 0
                      )
                        ? values.trips.status.find(
                          (item) => item.status === 0
                        ).count
                        : 0)}
                    bgColor={COLOR_GRAY}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
        </Container>
      )}
    </Fragment>
  );
}
